import { useState } from "react";
import { Outlet, Navigate, useNavigate } from "react-router-dom";
import { Sidebar, SidebarContent } from "@/components/layout/Sidebar";
import { Header } from "@/components/layout/Header";
import { VoiceNoteDrawer } from "@/components/VoiceNoteDrawer";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useFgp } from "@/context/FgpContext";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { Loader2, Eye } from "lucide-react";
import { toast } from "sonner";

export const AppShell = () => {
  const { authed, booting, viewAs, workspace, stopViewAs } = useFgp();
  const navigate = useNavigate();
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [exiting, setExiting] = useState(false);

  if (booting) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 gap-6" data-testid="app-booting">
      <Logo className="h-24" />
      <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading your workspace…</div>
    </div>
  );
  if (!authed) return <Navigate to="/login" replace />;

  const exitViewAs = async () => {
    setExiting(true);
    try { await stopViewAs(); toast.success("Returned to Platform Owner"); navigate("/app/admin"); }
    catch { toast.error("Could not exit view-as"); }
    finally { setExiting(false); }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />

      {/* Mobile nav drawer */}
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" className="w-72 p-0 flex flex-col lg:hidden" data-testid="mobile-sidebar">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SidebarContent onNavigate={() => setMobileNavOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex-1 flex flex-col min-w-0">
        {viewAs && (
          <div className="bg-amber-500 text-white px-4 sm:px-6 py-2.5 flex items-center gap-3 text-sm font-medium" data-testid="view-as-banner">
            <Eye className="h-4 w-4 shrink-0" />
            <span className="truncate">Viewing <b>{workspace?.name}</b> as the platform owner — read only. Changes are disabled.</span>
            <button onClick={exitViewAs} disabled={exiting} data-testid="exit-view-as"
              className="ml-auto shrink-0 inline-flex items-center gap-1.5 bg-white/20 hover:bg-white/30 transition-colors rounded-full px-3 py-1 text-xs font-semibold">
              {exiting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Return to Platform Owner
            </button>
          </div>
        )}
        <Header onVoiceClick={() => setVoiceOpen(true)} onMenuClick={() => setMobileNavOpen(true)} />
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-8 max-w-7xl w-full mx-auto">
          <motion.div
            key={window.location.pathname}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            <Outlet context={{ openVoice: () => setVoiceOpen(true) }} />
          </motion.div>
        </main>
      </div>
      <VoiceNoteDrawer open={voiceOpen} onOpenChange={setVoiceOpen} />
    </div>
  );
};
